// components
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { ConfirmationDialog, Dialog } from 'components/shared';
import AbsenceEmployeeField from './AbsenceEmployeeField';
import DateStepperField from 'components/shared/DateStepperField/DateStepperField';
import BenefitChangeNotice from './BenefitChangeNotice';
import ConsequencesPanel from './ConsequencesPanel';
import ReasonField from './ReasonField';
import WorkAgreementSection from './WorkAgreementSection';

// hooks
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRegisterAbsenceDialog } from './useRegisterAbsenceDialog';

// styles
import {
  Column,
  DialogActionsRow,
  DialogColumns,
  HintText,
  NoticeBanner,
  OverlapBox,
  OverlapTitle,
  PeriodRow,
  RequiredHint,
  SectionPanel,
  SectionTitle
} from './RegisterAbsenceDialog.styles';

// utils
import { formatByLocale, parseBackendDateTime } from 'utils/date';
import { endSentence } from './overlapPeriods';

// types
import type {
  RegisterAbsenceDialogEmployee,
  RegisterAbsenceDialogProps
} from './RegisterAbsenceDialog.types';

/**
 * React replacement of the Dojo "Afwezigheid vastleggen" dialog for the
 * absence-notice flows: sickness only (ZK/ZKZ), an explicit work agreement
 * choice, and a live panel showing the consequences for the planning.
 * Enabled through the FEATURE_TOGGLE_REACT_REGISTER_ABSENCE grant.
 */
const RegisterAbsenceDialog = ({
  open,
  employee: givenEmployee,
  notice,
  absenceId,
  onClose,
  onSuccess
}: RegisterAbsenceDialogProps) => {
  const { t } = useTranslation('common');
  const [confirmNoConsequences, setConfirmNoConsequences] = useState(false);
  // a new notice from the overview opens without an employee; the one picked
  // here stays fixed, a different employee means starting over
  const [pickedEmployee, setPickedEmployee] =
    useState<RegisterAbsenceDialogEmployee>();
  const employee = givenEmployee ?? pickedEmployee;
  const isWaitingForEmployee = employee === undefined;
  const form = useRegisterAbsenceDialog({
    open,
    employee,
    initialStartDate: notice ? new Date(notice.start) : undefined,
    initialEndDate: notice ? new Date(notice.end) : undefined,
    absenceId,
    onClose,
    onSuccess
  });

  const titleText = t(
    form.isEdit
      ? 'reactRegisterAbsence.editTitle'
      : 'reactRegisterAbsence.title'
  );
  const title = employee
    ? `${titleText} — ${[
        employee.name,
        employee.number ? `(${employee.number})` : null
      ]
        .filter(Boolean)
        .join(' ')}`
    : titleText;

  return (
    <Dialog
      open={open}
      title={title}
      handleClose={onClose}
      maxWidth={false}
      // tells this dialog apart from the confirmation dialog it can open
      data-testid="register-absence-dialog"
      actions={
        <DialogActionsRow>
          <Button onClick={onClose}>{t('cancel')}</Button>
          <Button
            variant="contained"
            disabled={!form.canSave}
            onClick={() => {
              // saving without any effect on the planning is possible,
              // but never silently; editing without changes is harmless
              if (!form.isEdit && form.hasNoConsequences) {
                setConfirmNoConsequences(true);
                return;
              }
              form.save();
            }}
          >
            {t('save')}
          </Button>
        </DialogActionsRow>
      }
    >
      <Box sx={{ p: 2 }}>
        {notice && (
          <NoticeBanner>
            {t(
              notice.reason
                ? 'reactRegisterAbsence.noticeBannerWithReason'
                : 'reactRegisterAbsence.noticeBanner',
              {
                name: notice.createdBy ?? employee?.name ?? '',
                from: formatByLocale(
                  parseBackendDateTime(notice.start),
                  'd MMM'
                ),
                to: formatByLocale(parseBackendDateTime(notice.end), 'd MMM'),
                reason: notice.reason
              }
            )}
          </NoticeBanner>
        )}

        <DialogColumns>
          {/* left column: what and for whom. Scrolls inside the fixed dialog
              height, so unfolding the working-days box grows this column
              instead of the dialog */}
          <Column sx={{ overflowY: 'auto', pr: 0.5 }}>
            {isWaitingForEmployee && (
              <SectionPanel data-testid="absence-employee-section">
                <SectionTitle>
                  {t('reactRegisterAbsence.employeeSection')}
                </SectionTitle>
                <AbsenceEmployeeField
                  enabled={open}
                  autoFocus
                  onSelect={setPickedEmployee}
                />
              </SectionPanel>
            )}
            <SectionPanel inert={isWaitingForEmployee}>
              <SectionTitle>
                {t('reactRegisterAbsence.periodSection')}
              </SectionTitle>
              {/* the two dates stay side by side; only the day count moves to
                  the next line when a long period ("105 dagen") needs the
                  room, so the period never breaks in two */}
              <PeriodRow
                sx={{
                  flexWrap: 'wrap',
                  columnGap: 2,
                  rowGap: 1,
                  alignItems: 'start'
                }}
              >
                {/* Bounds belong to editing, where the period may only shrink.
                    Registering leaves the dates free and greys out the days a
                    registered absence already covers: such a day may still be
                    chosen, because the overlap has to stay visible in the
                    consequences instead of being moved out of sight. */}
                <DateStepperField
                  testId="absence-period-from"
                  label={t('reactRegisterAbsence.from')}
                  date={form.startDate}
                  minDate={form.originalStartDate ?? undefined}
                  maxDate={form.originalEndDate ?? undefined}
                  shouldMuteDate={form.isRegisteredAbsenceDay}
                  onChange={form.setStartDate}
                  error={
                    // a cleared date blocks Save, so it names itself
                    !form.startDate
                      ? {
                          type: 'required',
                          message: t('reactRegisterAbsence.dateRequired')
                        }
                      : form.startBeforeOriginal
                      ? {
                          type: 'validate',
                          message: t('reactRegisterAbsence.startBeforeOriginal')
                        }
                      : undefined
                  }
                  previousDayLabel={t('reactRegisterAbsence.previousDay')}
                  nextDayLabel={t('reactRegisterAbsence.nextDay')}
                />
                <DateStepperField
                  testId="absence-period-to"
                  label={t('reactRegisterAbsence.to')}
                  date={form.endDate}
                  minDate={form.originalStartDate ?? undefined}
                  maxDate={form.originalEndDate ?? undefined}
                  shouldMuteDate={form.isRegisteredAbsenceDay}
                  onChange={form.setEndDate}
                  error={
                    // a cleared date blocks Save, so it names itself
                    !form.endDate
                      ? {
                          type: 'required',
                          message: t('reactRegisterAbsence.dateRequired')
                        }
                      : form.endBeforeStart
                      ? {
                          type: 'validate',
                          message: t('reactRegisterAbsence.endBeforeStart')
                        }
                      : form.endAfterOriginal
                      ? {
                          type: 'validate',
                          message: t('reactRegisterAbsence.endAfterOriginal')
                        }
                      : undefined
                  }
                  previousDayLabel={t('reactRegisterAbsence.previousDay')}
                  nextDayLabel={t('reactRegisterAbsence.nextDay')}
                />
              </PeriodRow>
              {/* said at the period, where the planner can still let the
                  absence stop where the inschaling does */}
              <BenefitChangeNotice
                changes={form.periodBenefitChanges}
                onEndAtBenefitEnd={form.endAtBenefitEnd}
              />
              {/* editing can only shrink the period; the original period is
                  the reference the planner shrinks within */}
              {form.isEdit &&
                form.originalStartDate &&
                form.originalEndDate && (
                  <HintText sx={{ mt: 1.5, mb: 0 }}>
                    {t('reactRegisterAbsence.originalPeriodHint', {
                      from: formatByLocale(
                        form.originalStartDate,
                        'd MMM yyyy'
                      ),
                      to: formatByLocale(form.originalEndDate, 'd MMM yyyy')
                    })}
                  </HintText>
                )}
              {/* saving is pointless here, so this is said at the source (the
                  period) instead of only through a disabled save button */}
              {form.overlappingAbsences.length > 0 && (
                <OverlapBox>
                  {/* one sentence: which work agreement it is adds nothing the
                      planner can act on, and the days at stake are told by the
                      greyed rows in the consequences panel and the bounds on
                      the fields above — neither is repeated here */}
                  <OverlapTitle>
                    {t('reactRegisterAbsence.overlapWarning', {
                      periods: endSentence(
                        form.overlappingAbsences
                          .map((absence) =>
                            t('reactRegisterAbsence.overlapWarningPeriod', {
                              from: formatByLocale(
                                parseBackendDateTime(absence.start),
                                'd MMM'
                              ),
                              to: formatByLocale(
                                parseBackendDateTime(absence.end),
                                'd MMM'
                              )
                            })
                          )
                          .join(', ')
                      )
                    })}
                  </OverlapTitle>
                </OverlapBox>
              )}
              <TextField
                select
                size="small"
                sx={{ mt: 2, minWidth: 240 }}
                label={t('reactRegisterAbsence.dutyHourType')}
                value={form.dutyTypeId}
                onChange={(event) =>
                  form.setDutyTypeId(Number(event.target.value))
                }
                // without a type Save is blocked; an empty list is a
                // configuration matter the planner cannot solve here, so it is
                // named rather than left as a grey button
                error={
                  form.hasDutyHourTypesResult && !form.dutyHourTypes.length
                }
                helperText={
                  form.hasDutyHourTypesResult && !form.dutyHourTypes.length
                    ? t('reactRegisterAbsence.noDutyHourTypes')
                    : undefined
                }
              >
                {form.dutyHourTypes.map((dutyHourType) => (
                  <MenuItem
                    key={dutyHourType.dutyTypeId}
                    value={dutyHourType.dutyTypeId}
                  >
                    {dutyHourType.description}
                    {dutyHourType.flags?.includes('REQ_DISCONNECT_AUTO') &&
                      ` ${t(
                        'reactRegisterAbsence.dutyHourTypeDetachesSuffix'
                      )}`}
                  </MenuItem>
                ))}
              </TextField>
            </SectionPanel>

            <SectionPanel inert={isWaitingForEmployee}>
              <SectionTitle>
                {t('reactRegisterAbsence.workAgreementSection')}
              </SectionTitle>
              {form.isEdit ? (
                // the backend cannot move an absence to another work
                // agreement or percentage, so editing shows them fixed
                <>
                  {form.workAgreements.map((workAgreement) => (
                    <Box key={workAgreement.waId} sx={{ fontSize: 14 }}>
                      {/* same line as when registering, contract kind
                          included: it explains the work agreement here too */}
                      {[
                        workAgreement.nr,
                        workAgreement.brName,
                        workAgreement.functionDescription,
                        workAgreement.contractKind,
                        `${form.percentage}%`
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Box>
                  ))}
                  <HintText sx={{ mt: 1, mb: 0 }}>
                    {t('reactRegisterAbsence.editWorkAgreementFixed')}
                  </HintText>
                </>
              ) : (
                <>
                  {/* the only reason a save can be blocked that is not visible
                      at its own field, so it is stated where the choice is */}
                  {form.selectedWaIds.length === 0 && (
                    <RequiredHint>
                      {t('reactRegisterAbsence.workAgreementRequired')}
                    </RequiredHint>
                  )}
                  <WorkAgreementSection
                    workAgreements={form.workAgreements}
                    selectedWaIds={form.selectedWaIds}
                    onChange={form.setSelectedWaIds}
                    dutyCountByWaId={form.dutyCountByWaId}
                    settings={form.workAgreementSettings}
                    onSettingChange={form.updateWorkAgreementSetting}
                    percentage={form.percentage}
                    onPercentageChange={form.setPercentage}
                    benefitChangeByWaId={form.benefitChangeByWaId}
                    isWorkingDaysChanged={form.isWorkingDaysChanged}
                    onSaveWorkingDays={(waId) =>
                      form.saveWorkingDaysSetting([waId])
                    }
                    periodStart={form.startDate}
                    periodEnd={form.endDate}
                    shouldMuteDate={form.isRegisteredAbsenceDay}
                  />
                </>
              )}
            </SectionPanel>
          </Column>

          {/* right column: the result, and the reason as the closing step */}
          <Column>
            {/* takes all remaining height, so as many duty lines as possible
                are visible without scrolling */}
            <SectionPanel
              inert={isWaitingForEmployee}
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0
              }}
            >
              <SectionTitle>
                {t('reactRegisterAbsence.consequencesSection')}
                <Box component="span" sx={{ fontWeight: 400, ml: 1 }}>
                  ({t('reactRegisterAbsence.consequencesLive')})
                </Box>
              </SectionTitle>
              <ConsequencesPanel
                existingDutyLines={form.existingDutyLines}
                newDutyLines={form.selectedNewDutyLines}
                selectedWaIds={form.selectedWaIds}
                workAgreements={form.workAgreements.map((workAgreement) => ({
                  waId: workAgreement.waId,
                  nr: workAgreement.nr,
                  planUnit: workAgreement.brName,
                  functionDescription: workAgreement.functionDescription,
                  planUnitMayChange:
                    !!form.benefitChangeByWaId[workAgreement.waId]
                }))}
                hasSelection={form.isEdit || form.selectedWaIds.length > 0}
                isLoading={
                  form.isEdit ? form.isLoadingAbsence : form.isRolloutLoading
                }
                hasResult={form.hasConsequencesResult}
                dutyHourTypeDescription={
                  form.dutyHourTypes.find(
                    (dutyHourType) =>
                      dutyHourType.dutyTypeId === form.dutyTypeId
                  )?.description
                }
                detachedGivesOpenDuty={form.detachedGivesOpenDuty}
                rolloutErrorKey={form.rolloutErrorKey}
                errorDutyLines={form.rolloutErrorDutyLines}
                overMaxHoursPerDay={form.rolloutOverMaxHoursPerDay}
                coveredPeriods={form.coveredAbsencePeriods}
                isEdit={form.isEdit}
              />
            </SectionPanel>

            {/* closing step: the planner's own note, pinned to the bottom */}
            <SectionPanel inert={isWaitingForEmployee} sx={{ mt: 'auto' }}>
              <SectionTitle>{t('reactRegisterAbsence.noteLabel')}</SectionTitle>
              {/* buffered: every keystroke would otherwise re-render the whole
                  dialog, which lags behind visibly with the developer tools
                  open. "verplicht veld" instead of an asterisk, so the
                  requirement is readable rather than a symbol to decode */}
              <ReasonField
                label={t('reactRegisterAbsence.requiredField')}
                value={form.note}
                onCommit={form.setNote}
                requiredMessage={t('reactRegisterAbsence.noteRequired')}
                highlightRequired={form.reasonIsOnlyBlocker}
              />
            </SectionPanel>
          </Column>
        </DialogColumns>
      </Box>

      {confirmNoConsequences && (
        <ConfirmationDialog
          type="warning"
          title={t('reactRegisterAbsence.noConsequencesTitle')}
          cancelBtnText={t('reactRegisterAbsence.goBack')}
          confirmBtnText={t('reactRegisterAbsence.processAnyway')}
          handleAction={(isConfirmed) => {
            setConfirmNoConsequences(false);
            if (isConfirmed) {
              form.save();
            }
          }}
        >
          {t('reactRegisterAbsence.noConsequencesBody')}
        </ConfirmationDialog>
      )}
    </Dialog>
  );
};

export default RegisterAbsenceDialog;
